/* eslint-disable camelcase */
export interface PreregistrationMinimal {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
}

import {User} from "./user";
import {ExamSession} from "./examSession";
import {ExamResultFile} from "./examResultFile";
import {Invoice} from "./invoice";
import {Certification} from "./certification";
import {ExamResultValue} from "./examResultValue";
import {ExamCenter} from "./examCenter";
import {ExamComment} from "./examComment";
import {CommentCategory, categoryByKey} from "../constants/exam-comment-categories";

/** Un badge de la colonne « Commentaires » : une catégorie, un jour, ses messages. */
export interface CommentBadge {
    label: string;
    badgeClass: string;
    count: number;
    /** La même catégorie a des commentaires d'un jour plus récent : badge affiché en transparence. */
    obsolete: boolean;
    /** Messages du groupe, du plus récent au plus ancien : contenu du survol. */
    comments: Array<ExamComment>;
}

interface CommentGroup {
    category: CommentCategory;
    comments: Array<ExamComment>;
}

// Jour local au format 2022-05-19 : clé de regroupement des commentaires.
function localDayKey (date: Date): string {
    return date.getFullYear() + "-" +
        String(date.getMonth() + 1).padStart(2, "0") + "-" +
        String(date.getDate()).padStart(2, "0");
}

/**
 * Vues dérivées des commentaires, mémoïsées tant que leur empreinte ne bouge pas.
 * Rendre un tableau neuf à chaque appel ferait détruire et recréer le DOM par les
 * `*ngFor` à chaque cycle de détection de changement : tooltip de badge qui ne
 * s'ouvre jamais, champ d'édition qui perd le focus à la frappe.
 */
interface CommentsCache {
    signature: string;
    badges: Array<CommentBadge>;
    reverseChronological: Array<ExamComment>;
}

export class Exam {

    id: number;

    file_number: string;

    voucher_code: string;

    session: ExamSession;

    student: User;

    success: boolean;

    examResultFiles: Array<ExamResultFile>;

    invoice: Invoice;

    certification: Certification;

    diplome: string;

    examResultValues: Array<ExamResultValue>;

    locked: boolean;

    can_pass: boolean;

    session_not_open_yet: boolean;
    session_already_closed: boolean;

    start_date: Date;

    exam_center: ExamCenter;

    comments: Array<ExamComment>;

    created_at: Date;

    assignedToPreregistrationAt: Date;

    convocation_sent_at: Date;

    studentConfirmedAt: Date;

    usedAt: Date;

    preregistration: PreregistrationMinimal | null;

    qualification_level: string;
    profession: string;
    objective: string;

    isMainCorrector: boolean;
    isAvailable: boolean;

    private commentsCache: CommentsCache = null;

    constructor (data?) {
        if (data) {
            this.build(data);
        }
    }

    // eslint-disable-next-line max-statements
    build (data) {
        this.id = data.id;
        this.voucher_code = data.voucher_code;
        this.file_number = data.file_number;
        this.isMainCorrector = data.is_main_corrector;
        this.isAvailable = data.is_available;

        if (data.session) {
            this.session = new ExamSession(data.session);
        }

        if (data.student) {
            this.student = new User(data.student);
        }

        this.success = data.success;

        if (data.exam_result_files) {
            this.examResultFiles = data.exam_result_files.map((f: object) => new ExamResultFile(f));
        }

        if (data.invoice) {
            this.invoice = new Invoice(data.invoice);
        }

        if (data.certification) {
            this.certification = new Certification(data.certification);
        }

        if (data.exam_result_values) {
            this.examResultValues = data.exam_result_values.map((v: object) => new ExamResultValue(v));
        }

        this.diplome = data.diplome;
        this.locked = data.locked;
        this.can_pass = data.can_pass;

        if (data.start_date) {
            this.start_date = new Date(data.start_date);
        }

        this.session_not_open_yet = data.session_not_open_yet;
        this.session_already_closed = data.session_already_closed;

        if (data.exam_center) {
            this.exam_center = new ExamCenter(data.exam_center);
        }

        if (data.comments) {
            this.comments = data.comments.map((c: unknown) => new ExamComment(c));
        }

        if (data.created_at) {
            this.created_at = new Date(data.created_at);
        }

        if (data.assigned_to_preregistration_at) {
            this.assignedToPreregistrationAt = new Date(data.assigned_to_preregistration_at);
        }

        if (data.convocation_sent_at) {
            this.convocation_sent_at = new Date(data.convocation_sent_at);
        }

        if (data.student_confirmed_at) {
            this.studentConfirmedAt = new Date(data.student_confirmed_at);
        }

        if (data.used_at) {
            this.usedAt = new Date(data.used_at);
        }

        this.preregistration = data.preregistration ?? null;

        this.qualification_level = data.qualification_level;
        this.profession = data.profession;
        this.objective = data.objective;
    }

    get hasResults () {
        return this.examResultFiles.length > 0;
    }

    get canBeReset () {
        return !this.hasResults && !this.locked && !this.diplome && this.examResultValues.length === 0;
    }

    // Même règle que Exam.can_be_rescheduled côté back
    get canBeRescheduled () {
        return Boolean(this.student) &&
            Boolean(this.exam_center) &&
            !this.locked &&
            !this.examResultFiles?.length &&
            !this.examResultValues?.length;
    }

    get canBeCorrected () {
        return this.session.wasOpen;
    }

    get isCorrected () {

        for (let index = 0; index < this.examResultValues.length; index += 1) {

            if (this.examResultValues[index].success !== null) {

                return true;

            }

        }

        return false;

    }

    toString (): string {
        return this.voucher_code;
    }

    getStatus (): string {
        if (this.locked) {
            return 'Verrouillé';
        }

        if (this.usedAt) {
            return 'Consommé le ' + this.usedAt.toLocaleDateString();
        }

        if (this.studentConfirmedAt) {
            return 'Confirmé le ' + this.studentConfirmedAt.toLocaleDateString();
        }

        if (this.convocation_sent_at) {
            return 'Convocation envoyée le ' + this.convocation_sent_at.toLocaleDateString();
        }

        if (this.assignedToPreregistrationAt) {
            return 'Assigné à une pré-inscription le ' + this.assignedToPreregistrationAt.toLocaleDateString();
        }

        return 'Créé le ' + this.created_at.toLocaleDateString();
    }

    // État de criticité du voucher dans l'onglet Tickets : un badge par
    // catégorie et par jour, avec son nombre de commentaires, triés par
    // criticité décroissante pour faire ressortir la gravité maximale en tête,
    // puis du jour le plus récent au plus ancien. Dans une catégorie, seul le
    // jour le plus récent est mis en avant : les jours précédents, supplantés,
    // sont en transparence. Dérivé des commentaires chargés pour rester juste
    // après un ajout/édition in-place.
    //
    // Un commentaire sans catégorie explicite est une « Observation » : c'est
    // l'absence de commentaire, et elle seule, qui ne donne aucun badge.
    get commentBadges (): Array<CommentBadge> {
        const cache = this.freshCommentsCache;

        if (!cache.badges) {
            cache.badges = this.buildCommentBadges();
        }

        return cache.badges;
    }

    // Commentaires du plus récent au plus ancien, ordre d'affichage de la modale.
    get reverseChronologicalComments (): Array<ExamComment> {
        const cache = this.freshCommentsCache;

        if (!cache.reverseChronological) {
            cache.reverseChronological = this.chronologicalComments.reverse();
        }

        return cache.reverseChronological;
    }

    // Cache des vues dérivées, remis à zéro dès que les commentaires changent.
    private get freshCommentsCache (): CommentsCache {
        const signature = this.commentsSignature;

        if (!this.commentsCache || this.commentsCache.signature !== signature) {
            this.commentsCache = {signature,
                "badges": null,
                "reverseChronological": null};
        }

        return this.commentsCache;
    }

    // Empreinte des commentaires : identité, catégorie, message et date,
    // c'est-à-dire tout ce dont les vues dérivées dépendent. Couvre les
    // mutations in-place faites par la modale (ajout, suppression, édition)
    // sans dépendre de la référence du tableau.
    private get commentsSignature (): string {
        if (!this.comments || !this.comments.length) {
            return "";
        }

        return this.comments.map((comment) => `${comment.id}|${comment.category}|${comment.comment}|${comment.created_date?.getTime()}`).join("\n");
    }

    private buildCommentBadges (): Array<CommentBadge> {
        const groups = new Map<string, CommentGroup>();

        // Parcours du plus récent au plus ancien : les groupes sont créés jour
        // par jour, du plus récent au plus ancien, et leurs messages dans
        // l'ordre de la modale.
        this.chronologicalComments.reverse().forEach((comment) => {
            const category = categoryByKey(comment.category);
            const key = `${category.key}|${localDayKey(comment.created_date)}`;
            const group = groups.get(key);

            if (group) {
                group.comments.push(comment);
            } else {
                groups.set(key, {category,
                    "comments": [comment]});
            }
        });

        // Le premier groupe rencontré d'une catégorie est son jour le plus
        // récent, les suivants sont obsolètes.
        const seenCategories = new Set<string | null>();

        return [...groups.values()].
            map((group) => {
                const obsolete = seenCategories.has(group.category.key);

                seenCategories.add(group.category.key);

                return {group,
                    obsolete};
            }).
            // Tri stable : l'ordre antichronologique est conservé dans une catégorie.
            sort((a, b) => b.group.category.severity - a.group.category.severity).
            map(({group, obsolete}) => ({
                "label": group.category.label,
                "badgeClass": group.category.badgeClass,
                "count": group.comments.length,
                obsolete,
                "comments": group.comments
            }));
    }

    private get chronologicalComments (): Array<ExamComment> {
        if (!this.comments || !this.comments.length) {
            return [];
        }

        return [...this.comments].sort((a, b) => a.created_date.getTime() - b.created_date.getTime());
    }

}
